import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, User, Sparkles } from "lucide-react";
import { useSettings } from "@/contexts/SettingsContext";
import SectionAccents from "@/components/SectionAccents";

const Founders = () => {
    const { settings } = useSettings();
    const f = settings?.founders || {};
    const members = f.members || [];
    const workshop = (f.workshop_images || []).filter(Boolean);

    const [openIdx, setOpenIdx] = useState(null);
    const close = useCallback(() => setOpenIdx(null), []);
    const prev = useCallback(() => setOpenIdx((i) => (i === null ? i : (i - 1 + workshop.length) % workshop.length)), [workshop.length]);
    const next = useCallback(() => setOpenIdx((i) => (i === null ? i : (i + 1) % workshop.length)), [workshop.length]);

    useEffect(() => {
        if (openIdx === null) return;
        const onKey = (e) => {
            if (e.key === "Escape") close();
            else if (e.key === "ArrowLeft") prev();
            else if (e.key === "ArrowRight") next();
        };
        document.body.style.overflow = "hidden";
        window.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = "";
            window.removeEventListener("keydown", onKey);
        };
    }, [openIdx, close, prev, next]);

    return (
        <div data-testid="founders-page">
            {/* Hero */}
            <section className="relative max-w-5xl mx-auto px-6 py-16 overflow-hidden">
                <SectionAccents variant="founders" />
                <div className="relative z-10">
                    <div className="inline-flex items-center gap-2 rounded-full glass px-3 py-1.5 text-xs font-medium text-primary mb-5">
                        <Sparkles className="w-3.5 h-3.5" /> Made by real humans
                    </div>
                    <h1 className="font-display text-4xl md:text-6xl font-bold tracking-tighter" data-testid="founders-headline">
                        {f.page_headline || "The team behind every miniature"}
                    </h1>
                    <p className="text-muted-foreground mt-4 max-w-2xl text-base md:text-lg" data-testid="founders-subheading">
                        {f.page_subheading || "A small studio in Bangalore obsessed with turning your memories into keepsakes."}
                    </p>
                </div>
            </section>

            {/* About */}
            {f.about_html && (
                <section className="max-w-4xl mx-auto px-6 pb-10" data-testid="founders-about">
                    <div className="rounded-3xl glass p-6 md:p-10 text-sm md:text-base leading-relaxed text-foreground/90 whitespace-pre-line">
                        {f.about_html}
                    </div>
                </section>
            )}

            {/* Team members */}
            <section className="relative max-w-6xl mx-auto px-6 py-10 overflow-hidden">
                <SectionAccents variant="craft" />
                <div className="relative z-10">
                    <div className="text-xs uppercase tracking-widest text-brand-gold font-semibold">Our people</div>
                    <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mt-1 mb-8">Founding team</h2>
                {members.length === 0 ? (
                    <div className="rounded-3xl glass p-10 text-center text-sm text-muted-foreground" data-testid="founders-members-empty">
                        Team profiles are being added. Check back soon 💛
                    </div>
                ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {members.map((m, i) => (
                            <motion.div
                                key={m.id || i}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.05 }}
                                data-testid={`founders-member-${i}`}
                                className="rounded-3xl glass p-5 flex flex-col items-center text-center hover:shadow-xl transition-shadow"
                            >
                                <div className="w-32 h-32 rounded-full overflow-hidden bg-primary/10 flex items-center justify-center mb-4 ring-4 ring-white/60 shadow-lg">
                                    {m.photo_url ? (
                                        <img src={m.photo_url} alt={m.name} className="w-full h-full object-cover" />
                                    ) : (
                                        <User className="w-12 h-12 text-primary/60" />
                                    )}
                                </div>
                                <div className="font-display text-xl font-semibold">{m.name}</div>
                                {m.role && <div className="text-xs uppercase tracking-widest text-brand-gold font-semibold mt-1">{m.role}</div>}
                                {m.bio && <p className="text-sm text-muted-foreground mt-3 leading-relaxed whitespace-pre-line">{m.bio}</p>}
                            </motion.div>
                        ))}
                    </div>
                )}
                </div>
            </section>

            {/* Workshop / printer gallery */}
            {workshop.length > 0 && (
                <section className="relative max-w-7xl mx-auto px-6 py-14 overflow-hidden">
                    <SectionAccents variant="sweet" />
                    <div className="relative z-10">
                        <div className="text-xs uppercase tracking-widest text-brand-gold font-semibold">Inside the studio</div>
                        <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mt-1 mb-8">The workshop</h2>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {workshop.map((src, i) => (
                            <motion.button
                                type="button"
                                key={i}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: (i % 6) * 0.05 }}
                                onClick={() => setOpenIdx(i)}
                                data-testid={`workshop-tile-${i}`}
                                className="rounded-3xl overflow-hidden aspect-square tilt-card cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-primary"
                            >
                                <img src={src} alt={`Workshop ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                            </motion.button>
                        ))}
                    </div>
                    </div>
                </section>
            )}

            {/* Lightbox */}
            <AnimatePresence>
                {openIdx !== null && workshop[openIdx] && (
                    <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        onClick={close}
                        data-testid="workshop-lightbox"
                        className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
                    >
                        <button onClick={(e) => { e.stopPropagation(); close(); }} data-testid="workshop-lightbox-close" aria-label="Close" className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"><X className="w-5 h-5" /></button>
                        {workshop.length > 1 && (
                            <>
                                <button onClick={(e) => { e.stopPropagation(); prev(); }} data-testid="workshop-lightbox-prev" aria-label="Previous" className="absolute left-2 sm:left-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"><ChevronLeft className="w-6 h-6" /></button>
                                <button onClick={(e) => { e.stopPropagation(); next(); }} data-testid="workshop-lightbox-next" aria-label="Next" className="absolute right-2 sm:right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"><ChevronRight className="w-6 h-6" /></button>
                            </>
                        )}
                        <motion.img
                            key={openIdx}
                            initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} transition={{ duration: 0.2 }}
                            src={workshop[openIdx]} alt="Workshop"
                            onClick={(e) => e.stopPropagation()}
                            data-testid="workshop-lightbox-image"
                            className="max-w-5xl max-h-[85vh] object-contain rounded-2xl shadow-2xl"
                        />
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default Founders;
